/**
 * Aktenraum: Inventarliste + Kurz-Stand für den 24B-Prompt.
 * Angehakte Auszüge (Cap wie Lazy-Extract v2) gehören in denselben LLM-Kontext —
 * nicht nur Dateinamen. Keine Voll-PDFs, kein PB, kein Klartext-Log.
 */

export const PORTAL_AKTE_INVENTORY_MAX_DOCS = 40;
export const PORTAL_AKTE_SELECTED_EXTRACTS_MAX = 5;
export const PORTAL_AKTE_FOLLOWUP_CONTEXT_MARKER = 'akte-kontext:followup-auszuege-v1';
export const PORTAL_AKTE_NO_FULL_PDF_HINT =
  'Hinweis: Für die angehakten Dateien liegt kein auslesbarer Text vor. Dateinamen sind nur Inventar und dürfen nicht als Dokumentinhalt behandelt werden.';

/** Ein Kurzabsatz, keine Running-Summary. */
export const PORTAL_AKTE_STAND_MAX_CHARS = 420;
export const PORTAL_AKTE_STAND_ORIENTATION_SNIPPET = 160;
export const PORTAL_AKTE_STAND_MAX_DOC_TITLES = 5;

/** Persistenter Kurz-Stand am ARKO-Thread (localStorage, gekoppelt an `akteId`). */
export interface PortalAkteThreadStand {
  selectedDocTitles: string[];
  focusTitle?: string | null;
  /** Letzte Frage + letzte ARKO-Orientierung, bereits gekürzt. */
  orientation: string;
  updatedAt: number;
}

export function collapseAkteStandText(raw: string, maxChars: number): string {
  const compact = raw.replace(/\s+/g, ' ').trim();
  if (!compact) return '';
  if (compact.length <= maxChars) return compact;
  const slice = compact.slice(0, maxChars);
  const lastWs = slice.lastIndexOf(' ');
  const cut = lastWs > maxChars * 0.6 ? slice.slice(0, lastWs) : slice;
  return `${cut.trimEnd()}…`;
}

export function buildAkteThreadStand(args: {
  selectedDocTitles: string[];
  focusTitle?: string | null;
  lastUserText?: string | null;
  lastBotText?: string | null;
  now?: number;
}): PortalAkteThreadStand {
  const titles = args.selectedDocTitles
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, PORTAL_AKTE_STAND_MAX_DOC_TITLES);
  const user = collapseAkteStandText(args.lastUserText || '', PORTAL_AKTE_STAND_ORIENTATION_SNIPPET);
  const bot = collapseAkteStandText(args.lastBotText || '', PORTAL_AKTE_STAND_ORIENTATION_SNIPPET);
  const parts: string[] = [];
  if (user) parts.push(`Frage: ${user}`);
  if (bot) parts.push(`ARKO: ${bot}`);
  const orientation = parts.length > 0
    ? collapseAkteStandText(parts.join(' '), PORTAL_AKTE_STAND_MAX_CHARS)
    : 'Noch kein Gespräch in dieser Akte.';
  return {
    selectedDocTitles: titles,
    focusTitle: args.focusTitle?.trim() || null,
    orientation,
    updatedAt: args.now ?? Date.now(),
  };
}

export function formatAkteStandParagraph(stand: PortalAkteThreadStand | null | undefined): string | null {
  if (!stand) return null;
  const chosen = stand.selectedDocTitles.length > 0
    ? stand.selectedDocTitles.join('; ')
    : '(keines explizit gewählt)';
  const focus = stand.focusTitle?.trim();
  const bits = [
    `Gewählte Dokumente: ${chosen}.`,
    focus ? `Fokus: ${focus}.` : null,
    `Letzte Orientierung: ${stand.orientation}`,
  ].filter(Boolean);
  return collapseAkteStandText(bits.join(' '), PORTAL_AKTE_STAND_MAX_CHARS);
}

export function previewAkteStandLabel(
  stand: PortalAkteThreadStand | null | undefined,
  inventoryCount?: number,
): string | null {
  if (!stand) return null;
  const n = typeof inventoryCount === 'number' && Number.isFinite(inventoryCount)
    ? Math.max(0, Math.floor(inventoryCount))
    : stand.selectedDocTitles.length;
  const docs = n === 1 ? '1 Doc' : `${n} Docs`;
  const orient = collapseAkteStandText(stand.orientation, 72);
  return `Stand: ${docs} · ${orient}`;
}

export function akteStandContentEquals(
  a?: PortalAkteThreadStand | null,
  b?: PortalAkteThreadStand | null,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.orientation === b.orientation
    && (a.focusTitle || '') === (b.focusTitle || '')
    && a.selectedDocTitles.join('\0') === b.selectedDocTitles.join('\0');
}

export function appendAkteStandToDocumentContext(
  documentContext: string | null | undefined,
  stand: PortalAkteThreadStand | null | undefined,
): string | null {
  const para = formatAkteStandParagraph(stand);
  const base = documentContext?.trim() || '';
  if (!para) return base || null;
  if (!base) return `Akten-Stand:\n${para}`;
  return `${base}\n\nAkten-Stand:\n${para}`;
}

export interface AkteSelectedExtract {
  title: string;
  extract: string;
}

/** LLM-Kontext hat echten Auszug — nicht nur Inventar/Dateinamen. */
export function aktePromptHasExtractBody(documentContext: string | null | undefined): boolean {
  const t = String(documentContext || '');
  if (!t.trim()) return false;
  return /Auszug \(UTF-8/.test(t)
    || /Auszug des Fokus-Dokuments/.test(t)
    || /Auszüge der angehakten Dokumente/.test(t);
}

/** Payload-Vertrag: schon ein erfolgreicher Auszug wird bei jedem Folge-Turn erneut gesendet. */
export function akteSelectedDocumentsForTurn<
  T extends { documentContext?: string | null },
>(
  selectedDocuments: readonly T[] | null | undefined,
  maxSelected = PORTAL_AKTE_SELECTED_EXTRACTS_MAX,
): T[] {
  const max = Number.isFinite(maxSelected) && maxSelected > 0
    ? Math.floor(maxSelected)
    : PORTAL_AKTE_SELECTED_EXTRACTS_MAX;
  return (selectedDocuments ?? [])
    .filter((doc) => aktePromptHasExtractBody(doc.documentContext))
    .slice(0, max);
}

export function buildAkteTurnContextPayload<
  T extends { documentContext?: string | null },
>(
  documentContext: string | null | undefined,
  selectedDocuments: readonly T[] | null | undefined,
  maxSelected = PORTAL_AKTE_SELECTED_EXTRACTS_MAX,
): { documentContext?: string; selectedDocuments?: T[] } {
  const context = documentContext?.trim() || '';
  const retained = akteSelectedDocumentsForTurn(selectedDocuments, maxSelected);
  return {
    ...(context ? { documentContext: context } : {}),
    ...(retained.length > 0 ? { selectedDocuments: retained } : {}),
  };
}

export function buildAkteInventoryDocumentContext(args: {
  akteTitle: string;
  documents: Array<{ title: string }>;
  focusTitle?: string | null;
  focusExtract?: string | null;
  standParagraph?: string | null;
  /** Angehakte Lazy-/OO-Auszüge (bereits gekürzt, Cap 5). */
  selectedExtracts?: AkteSelectedExtract[];
  /** Angehakt, aber OCR/Extract lieferte keinen Body — Dateiname bleibt nur Inventar. */
  selectedWithoutTextTitles?: string[];
  maxSelectedExtracts?: number;
}): string {
  const all = args.documents
    .map((d) => d.title.trim())
    .filter(Boolean);
  const listed = all.slice(0, PORTAL_AKTE_INVENTORY_MAX_DOCS);
  const extra = all.length - listed.length;
  const lines = [
    `Akte: ${args.akteTitle.trim() || 'Ohne Titel'}`,
    `Dokumente dieser Akte (${all.length}):`,
    ...(listed.length > 0 ? listed.map((title) => `- ${title}`) : ['- (noch keine zugeordneten Dokumente)']),
  ];
  if (extra > 0) {
    lines.push(`- … und ${extra} weitere (nur Metadaten, nicht im Prompt)`);
  }
  const focus = args.focusTitle?.trim();
  if (focus) {
    lines.push('', `Fokus: ${focus}`);
  }
  const maxSel = typeof args.maxSelectedExtracts === 'number' && args.maxSelectedExtracts > 0
    ? Math.floor(args.maxSelectedExtracts)
    : PORTAL_AKTE_SELECTED_EXTRACTS_MAX;
  const selected = (args.selectedExtracts ?? [])
    .map((s) => ({ title: s.title.trim(), extract: s.extract.trim() }))
    .filter((s) => s.extract.length > 0)
    .slice(0, maxSel);
  if (selected.length > 0) {
    lines.push('', 'Auszüge der angehakten Dokumente:');
    for (const s of selected) {
      lines.push('', `--- ${s.title || 'Dokument'} ---`, s.extract);
    }
  } else {
    const extract = args.focusExtract?.trim();
    if (extract) {
      lines.push('', 'Auszug des Fokus-Dokuments:', extract);
    } else {
      lines.push('', PORTAL_AKTE_NO_FULL_PDF_HINT);
    }
  }
  const withoutText = (args.selectedWithoutTextTitles ?? [])
    .map((title) => title.trim())
    .filter(Boolean)
    .slice(0, maxSel);
  if (withoutText.length > 0) {
    lines.push(
      '',
      'Angehakt, aber ohne auslesbaren Text (nicht aus Dateinamen ableiten):',
      ...withoutText.map((title) => `- ${title}`),
    );
  }
  const stand = args.standParagraph?.trim();
  if (stand) {
    lines.push('', 'Akten-Stand:', stand);
  }
  return lines.join('\n');
}

/**
 * Fokus-Auszug nur wenn der Anwalt das Fokus-Doc gehakt hat.
 * Unselected (z. B. nach Weiterarbeiten) darf den OO-Body nicht still in den Prompt ziehen.
 */
export function boundAkteFocusExtractIfSelected(args: {
  selectedIds: readonly string[];
  focusDocumentId?: string | null;
  focusBelongsToAkte: boolean;
  extract: string | null | undefined;
}): string | null {
  const focusId = args.focusDocumentId?.trim() || '';
  if (!args.focusBelongsToAkte || !focusId) return null;
  if (!args.selectedIds.some((id) => id.trim() === focusId)) return null;
  const extract = args.extract?.trim() || '';
  return extract || null;
}
